/** Deposit: pick the network → your address for it, with QR code, copy button and clear network label. */
import { Card, CopyButton, CardTitle, Notice, QrCode, Skeleton, cn } from "@custos/ui";
import { Check } from "lucide-react";
import { useSearchParams } from "react-router";
import { PageHeader } from "../components/AppShell";
import { AssetIcon } from "../components/bits";
import { FAMILY_KEY, findChain, networkLabel } from "../lib/chains";
import { errorMessage, isApiError } from "../lib/errors";
import { useConfig, useDepositAddress } from "../lib/queries";

export function DepositPage() {
  const config = useConfig();
  const [params, setParams] = useSearchParams();
  const chains = config.data?.chains ?? [];
  const assets = config.data?.assets ?? [];
  const selected = findChain(chains, params.get("chain") ?? "");
  const address = useDepositAddress(selected ? FAMILY_KEY[selected.family] : null);
  const chainAssets = assets.filter((a) => a.chain === selected?.key);

  return (
    <>
      <PageHeader
        title="Deposit"
        subtitle="Choose the network you're sending from, then send to your address."
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <Card className="p-2 sm:p-2">
          <p
            className="px-3 pt-2 pb-1 text-xs font-medium tracking-wide text-muted uppercase"
            id="network-picker"
          >
            Network
          </p>
          <div role="radiogroup" aria-labelledby="network-picker" className="flex flex-col gap-1">
            {chains.map((c) => {
              const active = c.key === selected?.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setParams({ chain: c.key }, { replace: true })}
                  className={cn(
                    "flex min-h-14 items-center gap-3 rounded-field px-3 text-left transition-colors duration-150",
                    active ? "bg-primary-soft ring-2 ring-primary" : "hover:bg-hover",
                  )}
                >
                  <AssetIcon symbol={c.nativeSymbol} className="size-10 text-base" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-fg">{c.displayName}</span>
                    <span className="block truncate text-xs text-muted">
                      {c.network} ·{" "}
                      {assets
                        .filter((a) => a.chain === c.key)
                        .map((a) => a.symbol)
                        .join(", ")}
                    </span>
                  </span>
                  {active && <Check className="size-4 text-primary-strong" aria-hidden />}
                </button>
              );
            })}
          </div>
        </Card>

        <Card>
          {!selected ? (
            <div className="flex min-h-48 items-center justify-center text-center text-sm text-muted">
              Pick a network to see your deposit address.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <CardTitle>Your {selected.displayName} address</CardTitle>
                <p className="mt-1 text-sm text-muted">
                  Network:{" "}
                  <span className="font-medium text-fg" data-testid="deposit-network">
                    {networkLabel(selected)}
                  </span>
                </p>
              </div>
              <Notice
                tone="warning"
                title={`Only send ${chainAssets.map((a) => a.symbol).join(" or ")} on ${networkLabel(selected)}`}
              >
                Coins sent on any other network, or other tokens, may be lost. Select{" "}
                <b>{selected.network}</b> in the wallet you send from.
              </Notice>
              {address.isPending && (
                <div className="flex flex-col items-center gap-3">
                  <Skeleton className="size-[208px]" />
                  <Skeleton className="h-6 w-full max-w-sm" />
                </div>
              )}
              {address.error && (
                <Notice tone="danger">
                  {isApiError(address.error, "DEPOSITS_UNAVAILABLE")
                    ? "Deposit addresses can't be created right now (the signing service is locked or offline). Please try again later."
                    : errorMessage(address.error)}
                </Notice>
              )}
              {address.data && (
                <div className="flex flex-col items-center gap-4">
                  <QrCode
                    value={address.data.address}
                    label={`QR code of your ${selected.displayName} deposit address`}
                  />
                  <div className="w-full rounded-field border border-line bg-soft p-3">
                    <p className="mb-1 text-xs text-muted">Address</p>
                    <div className="flex items-start gap-2">
                      <code
                        className="min-w-0 flex-1 font-mono text-sm break-all text-fg"
                        data-testid="deposit-address"
                      >
                        {address.data.address}
                      </code>
                      <CopyButton value={address.data.address} label="Copy address" />
                    </div>
                  </div>
                  {selected.family === "evm" && (
                    <p className="text-xs text-muted">
                      This same address also receives on{" "}
                      {address.data.chains
                        .filter((c) => c.chain !== selected.key)
                        .map((c) => c.displayName)
                        .join(", ")}{" "}
                      — always choose the network you want in your sending wallet.
                    </p>
                  )}
                </div>
              )}
              {chainAssets.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted">
                        <th className="py-1 pr-3 font-medium">Asset</th>
                        <th className="py-1 pr-3 font-medium">Minimum</th>
                        <th className="py-1 font-medium">Confirmations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chainAssets.map((a) => (
                        <tr key={a.symbol} className="border-t border-line">
                          <td className="py-2 pr-3 font-medium text-fg">{a.symbol}</td>
                          <td className="py-2 pr-3 font-mono text-fg">{a.minDeposit}</td>
                          <td className="py-2 font-mono text-fg">{a.minConfirmations}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-2 text-xs text-muted">Amounts below the minimum aren't credited.</p>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
